import React, { Fragment, useEffect, useMemo, useState } from 'react';
import EditableField from '../GrillaProyectos/EditableField';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Snackbar,
  Box,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ModalRating } from '../Modal/ModalRating';
import { iDataProyectosResultadoMap } from '../../interfaces/UmmiaApps/iDataProyectosResultadoMap';
import { iDataProyectoPorUsuario } from '../../interfaces/iDataProyectoPorUsuario';
import { calificarResultado } from '../../controllers/operaciones';
import { useAuthContext } from '../../hooks/Auth';
import SaveIcon from "@mui/icons-material/Save";
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { useChatUI } from '../UmmIAChatAI/ChatUIContext';

interface EditableFormProps {
  itemProyecto: iDataProyectosResultadoMap;
  index: number;
  labels: string[];
  handleCancelar: () => void;
  handleGuardar: (
    proyectoId: string,
    usuarioId: string,
    resultadoPropuestaId: string,
    proyecto: iDataProyectoPorUsuario['propuesta']
  ) => void;
}

const CHAT_COL_MD = 4; 
const TOP_STICKY = 16; 

const EditableForm: React.FC<EditableFormProps> = ({
  index,
  itemProyecto,
  labels,
  handleCancelar,
  handleGuardar,
}) => {
  const [expandedInfo, setExpandedInfo] = useState<Record<string | number, boolean>>({});
  const [proyecto, setProyecto] = useState<Record<string, any>>({});
  const [proyectoId, setProyectoId] = useState<string>();
  const [resultadoPropuestaId, setResultadoPropuestaId] = useState<string>();
  const [modalOpen, setModalOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [toast, setToast] = useState(false);
  const [unsavedFields, setUnsavedFields] = useState<Record<string, boolean>>({});

  const { user } = useAuthContext();

  

  // Chat context
  const { isChatOpen, openChat, closeChat } = useChatUI();

  const isEditMode = useMemo(
  () => Object.values(unsavedFields).some(Boolean),
  [unsavedFields]
);

const shouldShowChat = isChatOpen || isEditMode;

useEffect(() => {
  if (shouldShowChat) openChat();  
  else closeChat();
}, [shouldShowChat, openChat, closeChat]);

useEffect(() => {
  if (isEditMode) openChat();
  else closeChat();
}, [isEditMode, openChat, closeChat]);


  useEffect(() => {
    setProyecto(itemProyecto.propuesta);
    setProyectoId(itemProyecto.proyectoId);
    setResultadoPropuestaId(itemProyecto.resultadoPropuestaId);
  }, [itemProyecto]);

  const handleEditStart = (field: string) => {
    setUnsavedFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleEditCancel = (_field: string) => {
    setUnsavedFields({});
  };

  const handleSaveItem = (field: string, newValue: any) => {
    setProyecto((prevState) => ({ ...prevState, [field]: newValue }));
    setUnsavedFields((prev) => ({ ...prev, [field]: false }));
  };

  const toggleExpandedInfo = (i: string | number) => {
    setExpandedInfo((prevExpandedInfo) => ({
      ...prevExpandedInfo,
      [i]: !prevExpandedInfo[i],
    }));
  };

  const handleSaveProject = async () => {
    const hasUnsaved = Object.values(unsavedFields).some((v) => v);
    if (hasUnsaved) {
      alert('Hay contenido sin guardar. Por favor, guarde todas las ediciones antes de continuar.');
      return;
    }
    const { usuarioId } = itemProyecto;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (proyectoId && resultadoPropuestaId) {
      handleGuardar(proyectoId, usuarioId, resultadoPropuestaId, proyecto);
    }
  };

  const handleClose = () => {
    const hasUnsaved = Object.values(unsavedFields).some((v) => v);
    if (hasUnsaved && !window.confirm('Existen cambios sin guardar. ¿Desea cancelar sin guardar?')) return;
    handleCancelar();
  };

  const handleCalificar = async () => {
    if (rating && resultadoPropuestaId) {
      await calificarResultado(resultadoPropuestaId, rating);
    }
    setModalOpen(false);
    setToast(true);
    setTimeout(() => {
      setToast(false);
      handleCancelar();
    }, 2000);
  };

  const handleRating = async (n: number) => {
    setRating(n);
  };

  const renderEditableFields = (i: number) => {
    if (!labels.length || !proyecto) return null;
    return labels.map((label, keyIndex) => (
      <Fragment key={keyIndex}>
        <EditableField
          label={label.replace(/_/g, ' ')}
          value={proyecto[label]}
          onEditStart={handleEditStart}
          onEditCancel={handleEditCancel}
          onSave={(newValue: string | number) => handleSaveItem(label, newValue)}
        />
      </Fragment>
    ));
  };

  const CHAT_PANEL_WIDTH = 380;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        minHeight: '100dvh',
        px: { xs: 1, md: 2 },
        py: 2,
      }}
    >
      {/* Columna del chat*/}
      <Box
        component="aside"
        sx={{
          position: "fixed",
          left: 70,
          top: 70,
          height: "93dvh",
          width: shouldShowChat  ? CHAT_PANEL_WIDTH : 0,
          bgcolor: shouldShowChat  ? "background.paper" : "transparent",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
          zIndex: 10,

          borderRight: shouldShowChat  ? "1px solid #e0e0e0" : "none",
        }}
      >
        <Box
          sx={{
            p: isChatOpen ? 1.25 : 0,
            height: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
        
        </Box>
      </Box>

      {/* Columna del contenido principal */}
      <Box
        component="main"
        sx={{
          ml: shouldShowChat  ? `${CHAT_PANEL_WIDTH}px` : 0,
          transition: "margin-left 200ms ease",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <ModalRating
          modalOpen={modalOpen}
          rating={rating}
          handleRating={handleRating}
          handleGuardarCalificacion={handleCalificar}
          handleCloseModal={() => setModalOpen(false)}
        />

        {toast && (
          <Snackbar
            open={toast}
            autoHideDuration={1200}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setToast(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
              ¡Su calificación fue realizada exitosamente!
            </Alert>
          </Snackbar>
        )}

        <Box
          sx={{
            display: 'flex',
            m: { xs: 1, md: 2 },
            width: '100%',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ fontFamily: 'Inter', width: '100%' }}>
            {/** <IconButton onClick={handleClose} sx={{ float: 'right' }}>
              <CloseIcon />
            </IconButton>*/}

            {renderEditableFields(index)}

            {/**  <Button onClick={() => toggleExpandedInfo(index)}>
              {expandedInfo[index] ? 'Ver menos' : 'Ver más'}
            </Button>*/}

            {!isEditMode && (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
      mt: 2, mb: 3,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,  
        px: 1,
        borderRadius: 100,
        border: "1px solid #336B87",
        backgroundColor: "#1D3D4D",
        width: "fit-content", 
      }}
    >
      {/* Guardar */}
      <Tooltip
        title={itemProyecto.usuarioId === user?.id ? 'Guardar' : 'Guardar una copia para mi'}
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#1D3D4D",
              color: "#FFF",
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              padding: "8px 12px",
              borderRadius: "8px",
            },
          },
          arrow: { sx: { color: "#1D3D4D" } },
        }}
      >
        <Button
          onClick={handleSaveProject}
          variant="text"
          sx={{ borderRadius: 5, color: "#FFF" }}
        >
          <SaveIcon sx={{ fontSize: 25 }} />
        </Button>
      </Tooltip>

      {/* Cancelar */}
      <Tooltip
        title="Cancelar"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#1D3D4D",
              color: "#FFF",
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              padding: "8px 12px",
              borderRadius: "8px",
            },
          },
          arrow: { sx: { color: "#1D3D4D" } },
        }}
      >
        <Button
          onClick={handleClose}
          variant="text"
          sx={{ borderRadius: 5, color: "#FFF" }}
        >
          <CancelRoundedIcon sx={{ fontSize: 25 }} />
        </Button>
      </Tooltip>
    </Box>
  </Box>
)}



            {/**  {itemProyecto.usuarioId === user?.id && (
              <Button
                variant="contained"
                color="warning"
                onClick={() => setModalOpen(true)}
                sx={{ margin: '0 auto', display: 'block', width: '90%', mt: 1 }}
              >
                Calificar
              </Button>
            )}*/}

          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

export default EditableForm;
